// apps/web/src/components/chat/forms/MetricsForm.tsx
import { Button, TextField, Typography, Box, IconButton, Select, MenuItem, FormControl, InputLabel, Checkbox, FormControlLabel, Alert } from '@mui/material';
import { useState } from 'react';
import { Trash2, Plus } from 'lucide-react';
import { getSupabaseClient } from '@lingaai/supabase-client';
import { ICON_SIZES } from '../../../constants/icons';

interface Metric {
  name: string;
  description: string | null;
  data_type: 'number' | 'currency' | 'percentage' | 'text';
  is_required: boolean;
  display_order: number;
}

interface MetricsFormProps {
  data: {
    fund_id: string;
    metrics?: Metric[] | null;
  };
  tenantId: string;
  onSuccess: (count: number) => void;
  onClose: () => void;
}

export function MetricsForm({ data, tenantId, onSuccess, onClose }: MetricsFormProps) {
  const [formData, setFormData] = useState({
    ...data,
    metrics: data.metrics || [],
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const supabase = getSupabaseClient();

  const handleMetricChange = (idx: number, field: keyof Metric, value: any) => {
    const updatedMetrics = [...formData.metrics];
    updatedMetrics[idx] = { ...updatedMetrics[idx], [field]: value } as Metric;
    setFormData({ ...formData, metrics: updatedMetrics });
  };

  const handleAddMetric = () => {
    const newMetric: Metric = {
      name: '',
      description: null,
      data_type: 'number',
      is_required: false,
      display_order: formData.metrics.length + 1,
    };
    setFormData({
      ...formData,
      metrics: [...formData.metrics, newMetric],
    });
  };

  const handleRemoveMetric = (idx: number) => {
    const updatedMetrics = formData.metrics.filter((_, i) => i !== idx);
    // Reorder remaining metrics
    const reorderedMetrics = updatedMetrics.map((m, i) => ({
      ...m,
      display_order: i + 1,
    }));
    setFormData({ ...formData, metrics: reorderedMetrics });
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    setError(null);

    // Filter out empty metrics
    const validMetrics = formData.metrics.filter((m) => m.name.trim() !== '');

    if (validMetrics.length === 0) {
      setError('Please add at least one metric');
      setIsSaving(false);
      return;
    }

    // Prepare data for insertion
    const metricsToInsert = validMetrics.map((m) => ({
      tenant_id: tenantId,
      fund_id: formData.fund_id,
      name: m.name,
      description: m.description,
      data_type: m.data_type,
      is_required: m.is_required,
      display_order: m.display_order,
    }));

    try {
      const { data: insertedData, error: insertError } = await supabase.from('metric_definitions').insert(metricsToInsert).select();

      if (insertError) {
        throw insertError;
      }

      // Notify parent of success
      onSuccess(insertedData?.length || 0);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save metrics');
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ width: '100%', p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Define Metrics for Your Fund
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Review and customize the metrics you want to track. Add, remove, or edit as needed.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ flex: 1, overflowY: 'auto', mb: 2 }}>
        {formData.metrics.map((metric, idx) => (
          <Box
            key={idx}
            sx={{
              mb: 2,
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              position: 'relative',
            }}
          >
            <IconButton size="small" onClick={() => handleRemoveMetric(idx)} color="error" sx={{ position: 'absolute', top: 8, right: 8 }}>
              <Trash2 size={ICON_SIZES.lg} strokeWidth={1} />
            </IconButton>

            <Box sx={{ pr: 5 }}>
              <TextField fullWidth label="Metric Name" value={metric.name} onChange={(e) => handleMetricChange(idx, 'name', e.target.value)} size="small" required sx={{ mb: 2 }} />

              <TextField
                fullWidth
                label="Description"
                value={metric.description || ''}
                onChange={(e) => handleMetricChange(idx, 'description', e.target.value || null)}
                size="small"
                multiline
                rows={2}
                sx={{ mb: 2 }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ width: 200 }}>
                <InputLabel>Data Type</InputLabel>
                <Select value={metric.data_type} label="Data Type" onChange={(e) => handleMetricChange(idx, 'data_type', e.target.value)}>
                  <MenuItem value="number">Number</MenuItem>
                  <MenuItem value="currency">Currency</MenuItem>
                  <MenuItem value="percentage">Percentage</MenuItem>
                  <MenuItem value="text">Text</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel control={<Checkbox checked={metric.is_required} onChange={(e) => handleMetricChange(idx, 'is_required', e.target.checked)} />} label="Required" />
            </Box>
          </Box>
        ))}

        <Button startIcon={<Plus size={16} strokeWidth={1} />} onClick={handleAddMetric} variant="outlined" fullWidth sx={{ mt: 1 }}>
          Add Metric
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} fullWidth disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" fullWidth disabled={isSaving}>
          {isSaving ? 'Saving...' : `Save ${formData.metrics.filter((m) => m.name.trim() !== '').length} Metrics`}
        </Button>
      </Box>
    </Box>
  );
}
